import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { SecurityEvent } from "../types.js";
import SEOMeta from "../components/SEOMeta.js";


// Helper to safely parse event data
const parseEvent = (data: any): SecurityEvent => {
  return {
    id: data.id || '',
    eventYear: Number(data.eventYear) || 0,
    decade: data.decade || '',
    title: data.title || '',
    attackType: data.attackType || '',
    targetNode: data.targetNode || '',
    targetedAsset: data.targetedAsset || '',
    attacker: typeof data.attacker === 'string' ? JSON.parse(data.attacker) : (data.attacker || { name: '', organization: '', unit: '', node: '', city: '', country: '' }),
    victim: typeof data.victim === 'string' ? JSON.parse(data.victim) : (data.victim || { name: '', organization: '', unit: '', node: '', city: '', country: '' }),
    attackerArsenal: Array.isArray(data.attackerArsenal) ? data.attackerArsenal : (typeof data.attackerArsenal === 'string' ? JSON.parse(data.attackerArsenal) : []),
    victimSystemsAffected: Array.isArray(data.victimSystemsAffected) ? data.victimSystemsAffected : (typeof data.victimSystemsAffected === 'string' ? JSON.parse(data.victimSystemsAffected) : []),
    attackSequence: Array.isArray(data.attackSequence) ? data.attackSequence : (typeof data.attackSequence === 'string' ? JSON.parse(data.attackSequence) : []),
    trafficFlowAnalysis: data.trafficFlowAnalysis || '',
    barrierBypass: data.barrierBypass || '',
    executionOnTarget: data.executionOnTarget || '',
    strategicImpact: data.strategicImpact || '',
    operationalLosses: data.operationalLosses || '',
    detectionMoment: data.detectionMoment === 'Before Exploit' ? 'Before Exploit' : 'After Exploit',
    preAttackPosture: typeof data.preAttackPosture === 'object' ? data.preAttackPosture : (typeof data.preAttackPosture === 'string' ? JSON.parse(data.preAttackPosture) : { defenseStack: [], criticalLimitations: '' }),
    postAttackPosture: typeof data.postAttackPosture === 'object' ? data.postAttackPosture : (typeof data.postAttackPosture === 'string' ? JSON.parse(data.postAttackPosture) : { recoveryStack: [], containmentLimitations: '' }),
    strategicEvolution: typeof data.strategicEvolution === 'object' ? data.strategicEvolution : (typeof data.strategicEvolution === 'string' ? JSON.parse(data.strategicEvolution) : { incidentResponse: '', modernizedDefenseStrategy: '' }),
    persuasiveStory: data.persuasiveStory || '',
    references: Array.isArray(data.references) ? data.references : [],
    adSlot: data.adSlot,
    adClient: data.adClient,
    affiliateInfo: data.affiliateInfo
  };
};

// Types for search results
interface SearchResult {
  type: 'event' | 'year' | 'no-results';
  data: SecurityEvent | SecurityEvent[] | null;
  relevance: number;
}

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q")?.trim() || "";
  const [isScanning, setIsScanning] = useState(true);
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [suggestions, setSuggestions] = useState<SecurityEvent[]>([]);
  const [searchType, setSearchType] = useState<'loading' | 'exact-match' | 'partial-match' | 'year-search' | 'no-results'>('loading');

  // Fetch events from database/API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch('/api/events/all');
        if (response.ok) {
          const data = await response.json();
          // Parse all events
          const parsedEvents = data.map(parseEvent);
          setEvents(parsedEvents);
        } else {
          console.warn("API failed, showing empty results");
          setEvents([]);
        }
      } catch (error) {
        console.error("Failed to fetch events:", error);
        setEvents([]);
      } finally {
        setIsScanning(false);
      }
    };
    
    fetchEvents();
  }, []);

  // Generate suggestions based on partial input
  useEffect(() => {
    if (query.length < 2 || events.length === 0) {
      setSuggestions([]);
      return;
    }

    const normalizedQuery = query.toLowerCase();
    
    const matches = events.filter(event => 
      event.title.toLowerCase().includes(normalizedQuery) ||
      event.attackType?.toLowerCase().includes(normalizedQuery) ||
      event.victim.name?.toLowerCase().includes(normalizedQuery) ||
      event.attacker.name?.toLowerCase().includes(normalizedQuery)
    ).slice(0, 5);

    setSuggestions(matches);
  }, [query, events]);

  // Enhanced search logic
  const searchResults = useMemo((): SearchResult[] => {
    if (!query || events.length === 0) {
      if (query && query.length >= 2) {
        return [{
          type: 'no-results',
          data: null,
          relevance: 0
        }];
      }
      return [];
    }
    
    const normalizedQuery = query.toLowerCase();
    const results: SearchResult[] = [];

    // 1. Check for exact year search
    const yearMatch = normalizedQuery.match(/^(-?\d+)\s*(bce)?$/i);
    if (yearMatch) {
      const year = parseInt(yearMatch[1]);
      const isBCE = !!yearMatch[2];
      const yearNum = isBCE ? -year : year;
      const yearEvents = events.filter(e => e.eventYear === yearNum);
      
      if (yearEvents.length > 0) {
        results.push({
          type: 'year',
          data: yearEvents,
          relevance: 10
        });
        setSearchType('year-search');
      } else {
        results.push({
          type: 'no-results',
          data: null,
          relevance: 0
        });
        setSearchType('no-results');
      }
      return results;
    }

    // 2. Check for exact title match
    const exactTitleMatch = events.find(event => 
      event.title.toLowerCase() === normalizedQuery
    );
    if (exactTitleMatch) {
      results.push({
        type: 'event',
        data: exactTitleMatch,
        relevance: 10
      });
      setSearchType('exact-match');
      return results;
    }

    // 3. Check for events starting with query
    const startsWithMatches = events.filter(event =>
      event.title.toLowerCase().startsWith(normalizedQuery)
    ).sort((a, b) => b.eventYear - a.eventYear);

    if (startsWithMatches.length > 0) {
      if (startsWithMatches.length === 1) {
        results.push({
          type: 'event',
          data: startsWithMatches[0],
          relevance: 9
        });
        setSearchType('exact-match');
      } else {
        const yearGroups = startsWithMatches.reduce((groups, event) => {
          const year = event.eventYear;
          if (!groups[year]) groups[year] = [];
          groups[year].push(event);
          return groups;
        }, {} as Record<number, SecurityEvent[]>);

        Object.entries(yearGroups)
          .sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA))
          .forEach(([year, yearEvents]) => {
            if (yearEvents.length === 1) {
              results.push({
                type: 'event',
                data: yearEvents[0],
                relevance: 8
              });
            } else {
              results.push({
                type: 'year',
                data: yearEvents,
                relevance: 7
              });
            }
          });
        setSearchType('partial-match');
      }
      return results;
    }

    // 4. Check for partial matches in title
    const titlePartialMatches = events.filter(event =>
      event.title.toLowerCase().includes(normalizedQuery)
    );

    if (titlePartialMatches.length > 0) {
      titlePartialMatches
        .sort((a, b) => b.eventYear - a.eventYear)
        .forEach(event => {
          const startsWith = event.title.toLowerCase().indexOf(normalizedQuery) === 0;
          results.push({
            type: 'event',
            data: event,
            relevance: startsWith ? 6 : 5
          });
        });
      setSearchType('partial-match');
      return results;
    }

    // 5. Check for attack type matches
    const attackTypeMatches = events.filter(event =>
      event.attackType?.toLowerCase().includes(normalizedQuery)
    );

    if (attackTypeMatches.length > 0) {
      const yearGroups = attackTypeMatches.reduce((groups, event) => {
        const year = event.eventYear;
        if (!groups[year]) groups[year] = [];
        groups[year].push(event);
        return groups;
      }, {} as Record<number, SecurityEvent[]>);

      Object.entries(yearGroups)
        .sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA))
        .forEach(([year, yearEvents]) => {
          results.push({
            type: 'year',
            data: yearEvents,
            relevance: 4
          });
        });
      setSearchType('partial-match');
      return results;
    }

    // 6. Check for victim/attacker name matches
    const nameMatches = events.filter(event =>
      event.victim.name?.toLowerCase().includes(normalizedQuery) ||
      event.attacker.name?.toLowerCase().includes(normalizedQuery)
    );

    if (nameMatches.length > 0) {
      nameMatches
        .sort((a, b) => b.eventYear - a.eventYear)
        .forEach(event => {
          results.push({
            type: 'event',
            data: event,
            relevance: 3
          });
        });
      setSearchType('partial-match');
      return results;
    }

    // 7. No matches found
    results.push({
      type: 'no-results',
      data: null,
      relevance: 0
    });
    setSearchType('no-results');
    return results;
  }, [query, events]);

  // Auto-redirect logic
  useEffect(() => {
    if (searchResults.length === 0 || isScanning) return;

    const timer = setTimeout(() => {
      const result = searchResults[0];
      
      if (result.type === 'event' && result.data && !Array.isArray(result.data)) {
        navigate(`/event/${result.data.id}`, { replace: true });
      } 
      else if (result.type === 'year' && Array.isArray(result.data) && result.data.length > 0) {
        navigate(`/year/${result.data[0].eventYear}`, { replace: true });
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [searchResults, navigate, isScanning]);

  // Handle suggestion click
  const handleSuggestionClick = (event: SecurityEvent) => {
    navigate(`/event/${event.id}`);
  };

  // Handle view all in year
  const handleViewYear = (year: number) => {
    navigate(`/year/${year}`);
  };

  if (isScanning) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center space-y-6">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 border-2 border-blue-600/10 rounded-full"></div>
          <div className="absolute inset-0 border-2 border-t-blue-600 rounded-full animate-spin"></div>
          <div className="absolute inset-4 bg-blue-600/10 rounded-full animate-pulse"></div>
        </div>
        <div className="text-center space-y-2">
          <p className="text-[10px] font-black font-mono uppercase tracking-[0.4em] text-blue-500 animate-pulse">
            Scanning Database...
          </p>
          <p className="text-[9px] font-bold text-slate-600 uppercase tracking-widest italic">
            Query: "{query}"
          </p>
        </div>
      </div>
    );
  }

  // Calculate total matches
  const totalMatches = searchResults.reduce((total, result) => {
    if (result.type === 'event') return total + 1;
    if (result.type === 'year' && Array.isArray(result.data)) return total + result.data.length;
    return total;
  }, 0);


  const canonicalUrl = query ? `https://cyberannals.com/search?q=${encodeURIComponent(query)}` : "https://cyberannals.com/search";

  return (
    <>
      <SEOMeta
        title={`${totalMatches} Search Results for "${query}" | Cyber Security Historical Database`}
        description={`Found ${totalMatches} matches for "${query}" in our cyber security events database. Historical attacks, data breaches, and security incidents analysis.`}
        keywords={`${query} cyber attack, ${query} security breach, search cybersecurity database, historical cyber attacks search`}
        canonical={canonicalUrl}
        noindex={query.length < 3}
      />
      
      <div className="space-y-8 py-8 animate-in duration-500 max-w-6xl mx-auto px-4">
        {/* Search Header */}
        <div className="space-y-4">
          <nav className="flex items-center gap-3 text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">
            <Link to="/" className="hover:text-blue-500 transition-colors">
              Home
            </Link>
            <i className="fa-solid fa-chevron-right text-[7px] opacity-30"></i>
            <Link to="/decades" className="hover:text-blue-500 transition-colors">
              Decades
            </Link>
            <i className="fa-solid fa-chevron-right text-[7px] opacity-30"></i>
            <span className="text-white">Search: "{query}"</span>
          </nav>

          <div className="space-y-2">
            <h1 className="text-3xl sm:text-6xl font-black italic tracking-tighter uppercase text-white leading-none">
              {totalMatches} {totalMatches === 1 ? 'Match' : 'Matches'} Found
            </h1>
            <p className="text-[11px] font-black uppercase tracking-[0.4em] text-slate-500">
              {searchType === 'exact-match' && 'Exact match found'}
              {searchType === 'partial-match' && 'Partial matches found'}
              {searchType === 'year-search' && 'Year-specific events'}
              {searchType === 'no-results' && 'No matches found'}
            </p>
          </div>
        </div>

        {/* Results Container */}
        <div className="space-y-8">
          {searchResults.map((result, index) => {
            // Render single event
            if (result.type === 'event' && result.data && !Array.isArray(result.data)) {
              const event = result.data;
              const displayYear = event.eventYear < 0 ? `${Math.abs(event.eventYear)} ` : event.eventYear;
              
              return (
                <div key={event.id} className="animate-in slide-in-from-bottom-4" style={{ animationDelay: `${index * 100}ms` }}>
                  <Link
                    to={`/event/${event.id}`}
                    className="flex flex-col md:flex-row gap-6 p-8 bg-white/5 border border-white/5 rounded-3xl hover:border-blue-500 hover:bg-white/[0.08] transition-all group"
                  >
                    <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-xl bg-slate-900 text-blue-400 group-hover:bg-blue-600 group-hover:text-white font-black text-lg transition-all border border-white/5">
                      {String(index + 1).padStart(2, "0")}
                    </div>
                    <div className="space-y-3 flex-grow">
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-blue-600 text-white text-[8px] font-black rounded uppercase tracking-widest">
                          {event.attackType}
                        </span>
                        <span className="px-3 py-1 bg-white/5 text-slate-400 text-[8px] font-black rounded uppercase tracking-widest border border-white/5">
                          {displayYear}
                        </span>
                        {result.relevance >= 9 && (
                          <span className="px-3 py-1 bg-green-500/10 text-green-400 text-[8px] font-black rounded uppercase tracking-widest border border-green-500/20">
                            Exact Match
                          </span>
                        )}
                      </div>
                      <h2 className="text-2xl font-black uppercase tracking-tighter group-hover:text-blue-400 transition italic leading-none text-white">
                        {event.title}
                      </h2>
                      <div className="flex items-center gap-4 text-[12px] text-slate-400">
                        <span className="flex items-center gap-1">
                          <i className="fa-solid fa-user-secret"></i>
                          {event.attacker.name}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="fa-solid fa-building"></i>
                          {event.victim.name}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-950 text-blue-500 border border-white/5 group-hover:bg-blue-600 group-hover:text-white transition-all">
                      <i className="fa-solid fa-arrow-right text-[12px]"></i>
                    </div>
                  </Link>
                </div>
              );
            }

            // Render year container with multiple events
            if (result.type === 'year' && Array.isArray(result.data) && result.data.length > 0) {
              const yearEvents = result.data;
              const year = yearEvents[0].eventYear;
              const displayYear = year < 0 ? `${Math.abs(year)} ` : year;

              return (
                <div key={`year-${year}`} className="animate-in slide-in-from-bottom-4" style={{ animationDelay: `${index * 100}ms` }}>
                  <div className="bg-white/5 border border-white/5 rounded-3xl overflow-hidden">
                    {/* Year Header */}
                    <div className="p-6 border-b border-white/10 bg-white/[0.02]">
                      <div className="flex justify-between items-center">
                        <div>
                          <h2 className="text-2xl font-black text-white">
                            {displayYear} Events
                          </h2>
                          <p className="text-[11px] text-slate-400 mt-1">
                            {yearEvents.length} events found
                          </p>
                        </div>
                        <button
                          onClick={() => handleViewYear(year)}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-black uppercase tracking-widest rounded-lg transition-all"
                        >
                          View All
                        </button>
                      </div>
                    </div>

                    {/* Events List */}
                    <div className="divide-y divide-white/10">
                      {yearEvents.map((event, eventIndex) => (
                        <Link
                          key={event.id}
                          to={`/event/${event.id}`}
                          className="flex items-center gap-4 p-6 hover:bg-white/[0.03] transition-colors group"
                        >
                          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900 text-slate-400 group-hover:text-blue-400 text-[10px] font-bold">
                            {eventIndex + 1}
                          </div>
                          <div className="flex-grow">
                            <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors">
                              {event.title}
                            </h3>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <span className="text-[11px] px-2 py-1 bg-blue-500/10 text-blue-400 rounded">
                                {event.attackType}
                              </span>
                              <span className="text-[11px] text-slate-500">
                                {event.victim.name}
                              </span>
                            </div>
                          </div>
                          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="fa-solid fa-chevron-right text-blue-400"></i>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            // Render no results
            if (result.type === 'no-results') {
              return (
                <div key="no-results" className="py-32 text-center border-4 border-dashed border-white/5 rounded-[3rem] bg-white/5 space-y-8 animate-in fade-in">
                  <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mx-auto border border-white/10">
                    <i className="fa-solid fa-magnifying-glass-slash text-slate-700 text-4xl"></i>
                  </div>
                  <div className="space-y-3">
                    <h2 className="text-2xl font-black uppercase italic tracking-tighter text-white">
                      No Results Found
                    </h2>
                    <p className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.3em] max-w-sm mx-auto italic">
                      No records matching <span className="text-blue-500">"{query}"</span> found.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                      to="/decades"
                      className="px-8 py-3 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-blue-700 transition-all"
                    >
                      Browse by Decade
                    </Link>
                    <Link
                      to="/"
                      className="px-8 py-3 border border-white/10 text-slate-400 text-[10px] font-black uppercase tracking-widest rounded-xl hover:border-blue-500 hover:text-blue-400 transition-all"
                    >
                      Return Home
                    </Link>
                  </div>
                </div>
              );
            }

            return null;
          })}
        </div>

        {/* Search Suggestions */}
        {suggestions.length > 0 && searchType !== 'exact-match' && searchType !== 'no-results' && (
          <div className="mt-12 p-6 bg-white/5 border border-white/10 rounded-2xl">
            <h3 className="text-lg font-bold text-white mb-4">Related Searches</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {suggestions.map((suggestion) => {
                const displayYear = suggestion.eventYear < 0 ? 
                  `${Math.abs(suggestion.eventYear)} ` : suggestion.eventYear;
                
                return (
                  <button
                    key={suggestion.id}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="text-left p-4 bg-white/[0.03] hover:bg-white/[0.08] border border-white/5 rounded-xl transition-all group"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest">
                        {suggestion.attackType}
                      </span>
                      <span className="text-[9px] text-slate-500">
                        {displayYear}
                      </span>
                    </div>
                    <h4 className="text-sm font-bold text-white group-hover:text-blue-400 transition-colors mb-1">
                      {suggestion.title}
                    </h4>
                    <p className="text-[11px] text-slate-400">
                      {suggestion.victim.name}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SearchResults;